import { z } from 'zod';
import { asRecord } from '../../resultValues.js';

export const words = z.array(z.string()).default([]);
export const elementSchema = z.object({
  feature: z.string(),
  tag: z.string(),
  source: z.string(),
  source_label: z.string().optional(),
  recommended: z.boolean().optional(),
  potential: z.string().optional(),
  spend: z.number().optional(),
  spend_share: z.number().optional(),
  creative_count: z.number().int().nonnegative().optional(),
  context: z.string().optional(),
  figures: z
    .array(
      z.object({
        network: z.string(),
        network_label: z.string().optional(),
        kpi: z.number().nullable(),
        lift: z.number(),
        rank: z.number(),
        spend: z.number().optional(),
      }),
    )
    .default([]),
});
const categorySchema = z.object({ name: z.string(), elements: z.array(elementSchema) });
export const ideationBriefSchema = z.object({
  name: z.string(),
  body: z.string(),
  kpi: z.string(),
  kpi_label: z.string().optional(),
  creative_type: z.string(),
  campaign_objectives: words,
  concepts: words,
  formats: words,
  durations: words,
  categories: z.array(categorySchema),
  // Offered but not in the brief: ticking one and updating the brief keeps it.
  suggestions: z.array(categorySchema).default([]),
  empty_categories: words,
});
export const ideationResultSchema = z.object({ briefs: z.array(ideationBriefSchema) });

export type IdeationBrief = z.infer<typeof ideationBriefSchema>;
export type BriefElement = IdeationBrief['categories'][number]['elements'][number];
// A brief is storyboarded element by element: each key names one element of
// one brief, so the storyboard step receives only what was kept.
export interface ElementSelection {
  selectedKeys: string[];
  onToggle: (key: string) => void;
  onToggleAll: (keys: string[]) => void;
}

interface ElementIdentity {
  feature: string;
  tag: string;
}

export function elementKeyOf(
  briefName: string,
  category: string,
  element: ElementIdentity,
): string {
  return JSON.stringify([briefName, category, element.feature, element.tag]);
}

export function sameKeys(first: string[], second: string[]): boolean {
  const keys = new Set(first);
  return keys.size === new Set(second).size && second.every((key) => keys.has(key));
}

export function briefNameOfKey(key: string): string {
  const parsed: unknown = JSON.parse(key);
  return Array.isArray(parsed) && typeof parsed[0] === 'string' ? parsed[0] : '';
}

function keysOf(briefName: string, categories: IdeationBrief['categories']): string[] {
  return categories.flatMap((category) =>
    category.elements.map((element) => elementKeyOf(briefName, category.name, element)),
  );
}

// Everything the brief step can tick: what the brief holds and what it suggests.
export function elementKeysOf(brief: IdeationBrief): string[] {
  return [...keysOf(brief.name, brief.categories), ...keysOf(brief.name, brief.suggestions)];
}

export function keptKeysOf(brief: IdeationBrief): string[] {
  return keysOf(brief.name, brief.categories);
}

export function elementRefOfKey(key: string): { category: string; feature: string; tag: string } {
  const [, category, feature, tag] = z
    .tuple([z.string(), z.string(), z.string(), z.string()])
    .parse(JSON.parse(key));
  return { category, feature, tag };
}

function elementKeyOfRaw(briefName: string, category: string, element: unknown): string | null {
  const record = asRecord(element);
  const feature = record?.['feature'];
  const tag = record?.['tag'];
  return typeof feature === 'string' && typeof tag === 'string'
    ? elementKeyOf(briefName, category, { feature, tag })
    : null;
}

// Works on the raw result rather than the parsed one so every field the brief
// carries travels on to the storyboard, not just the ones this app reads.
export function narrowBriefs(result: unknown, selectedKeys: string[]): Record<string, unknown>[] {
  const briefs = asRecord(result)?.['briefs'];
  if (!Array.isArray(briefs)) return [];
  const kept = new Set(selectedKeys);
  return briefs.flatMap((entry) => {
    const brief = asRecord(entry);
    const name = brief?.['name'];
    const categories = brief?.['categories'];
    if (brief === null || typeof name !== 'string' || !Array.isArray(categories)) return [];
    let count = 0;
    const narrowed = categories.map((category) => {
      const record = asRecord(category);
      const elements = record?.['elements'];
      const categoryName = record?.['name'];
      if (record === null || typeof categoryName !== 'string' || !Array.isArray(elements))
        return category;
      const picked = elements.filter((element) => {
        const key = elementKeyOfRaw(name, categoryName, element);
        return key !== null && kept.has(key);
      });
      count += picked.length;
      return { ...record, elements: picked };
    });
    return count === 0 ? [] : [{ ...brief, categories: narrowed }];
  });
}
